import React from 'react';
import { Link } from 'react-router-dom';
import '../dialify.css';

const Terms: React.FC = () => {
  return (
    <div className="dialify-landing min-h-screen bg-[#F3F3F3] text-[#132D28]">
      <header className="fixed top-0 z-40 w-full border-b border-[#204941]/10 bg-[#F3F3F3]/90 backdrop-blur-sm">
        <nav className="container mx-auto py-4">
          <Link to="/" className="text-lg font-semibold text-[#204941]">Dialify</Link>
        </nav>
      </header>

      <main className="container mx-auto pb-16 pt-28">
        <article className="rounded-3xl bg-white px-6 py-10 shadow-sm md:px-12">
          <header className="mb-10">
            <h1 className="text-3xl font-semibold text-[#132D28] md:text-4xl">Terms of Use for Dialify</h1>
            <p className="mt-4 text-sm text-[#204941]/60">Effective as of 03 October 2025</p>
          </header>

          <section className="space-y-8 text-[#204941]/80">
            <div className="rounded-2xl border border-[#204941]/10 bg-[#F3F3F3] p-5">
              <h2 className="mb-2 text-lg font-semibold text-[#204941]">Friendly reminder</h2>
              <p className="text-sm text-[#204941]/70">
                Dialify helps you customize your Apple Watch. It does not affect Apple Watch system settings and it does not provide medical or diagnostic services.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">1. Agreement to terms</h2>
              <p>
                By installing, accessing, or using Dialify you agree to follow these terms and our Privacy Promise. If you disagree with any part, please remove Dialify from your device.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">2. What Dialify provides</h2>
              <p>
                We curate digital watch faces, wallpapers, widget packs, and companion tips for Apple Watch users. Some items are free; premium collections require an active subscription or one-time purchase.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">3. Account eligibility</h2>
              <p>
                You must be at least 13 years old to use Dialify. Users under 18 must have permission from a parent or guardian. Ensure your Apple ID details are accurate and kept secure.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">4. Purchases and billing</h2>
              <ul className="list-disc space-y-2 pl-5">
                <li>Payments are processed by Apple. We do not store your card details.</li>
                <li>Subscriptions renew automatically unless cancelled at least 24 hours before the period ends.</li>
                <li>You can manage or cancel a plan in iOS Settings → Apple ID → Subscriptions.</li>
                <li>Trial periods convert to paid plans if not cancelled before they expire.</li>
              </ul>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">5. License granted</h2>
              <p>
                We provide you with a personal, limited, revocable license to use Dialify for non-commercial purposes. You may not redistribute content packages, resell our watch faces, or reverse engineer the app.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">6. Creator uploads</h2>
              <p>
                Designers who submit watch faces warrant they own the rights to the content. By uploading, you grant Dialify a licence to host, showcase, and deliver the creative assets within the app and on our marketing channels.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">7. Acceptable use</h2>
              <ul className="list-disc space-y-2 pl-5">
                <li>Do not attempt to copy or scrape the service at scale.</li>
                <li>Avoid sharing offensive or infringing content.</li>
                <li>Respect other users and creators in community areas.</li>
                <li>Report bugs or vulnerabilities responsibly to <a href="mailto:dialify.app@gmail.com" className="text-[#204941] underline hover:text-[#68C1AE]">dialify.app@gmail.com</a>.</li>
              </ul>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">8. Intellectual property</h2>
              <p>
                Dialify, our name, logos, and the curated collections remain our property or that of respective creators. Apple, Apple Watch, and related marks belong to Apple Inc. and are used under fair use references.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">9. Warranty disclaimer</h2>
              <p>
                Dialify is provided “as is”. We aim for a smooth experience but cannot guarantee uninterrupted availability or error-free operation. Customizations are cosmetic; Apple Watch system performance is unchanged.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">10. Limitation of liability</h2>
              <p>
                To the extent allowed by law, Dialify and its team are not liable for indirect or consequential damages arising from use of the service. Our total liability is limited to the amount you paid during the previous 3 months.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">11. Ending the relationship</h2>
              <p>
                You may stop using Dialify at any time. We may suspend or terminate access if we detect misuse, unpaid charges, or legal obligations. Upon termination, licenses granted to you end immediately.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">12. Governing law</h2>
              <p>
                These terms are governed by the laws of Estonia, with disputes resolved in Tallinn courts, unless local consumer law states otherwise.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">13. Updates to the terms</h2>
              <p>
                We may modify these terms as Dialify evolves. Changes take effect when posted here. If the updates are material, we will notify you through the app or by email before they become active.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-[#204941]">14. Contact</h2>
              <p>
                Questions? Reach out to <a href="mailto:dialify.app@gmail.com" className="text-[#204941] underline hover:text-[#68C1AE]">dialify.app@gmail.com</a> and we will be happy to assist.
              </p>
            </div>
          </section>
        </article>
      </main>
    </div>
  );
};

export default Terms;
